import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login        from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard    from './pages/Dashboard';
import Generate     from './pages/Generate';
import History      from './pages/History';
import VideoPost    from './pages/VideoPost';
import Settings     from './pages/Settings';
import Channels     from './pages/Channels';
import Admin        from './pages/Admin';
import Datenschutz        from './pages/Datenschutz';
import Nutzungsbedingungen from './pages/Nutzungsbedingungen';
import Layout       from './components/Layout';

// ── Auth Context ──
export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

// ── Theme Context ──
export const ThemeContext = createContext(null);
export function useTheme() { return useContext(ThemeContext); }

// Globale Logout-Referenzen — werden von api() aufgerufen
let _globalLogout       = null; // 403 deactivated → Sperrseite
let _globalLogoutNormal = null; // 401 Token abgelaufen → Login mit Hinweis

function DsgvoModal({ onAccept }) {
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  async function handleAccept() {
    setLoading(true);
    try {
      await api('/auth/accept-dsgvo', { method: 'POST' }, token);
      onAccept();
    } catch {}
    setLoading(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--glass-intense, rgba(10,15,40,0.95))', border: '1px solid rgba(0,200,255,0.2)', borderRadius: 20, padding: 32 }}>
        <h2 style={{ fontFamily: 'Orbitron', fontSize: 16, color: 'var(--cyan, #00c8ff)', marginBottom: 12, marginTop: 0 }}>Datenschutz & Nutzungsbedingungen</h2>
        <p style={{ color: 'var(--text2, rgba(255,255,255,0.7))', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
          Bitte lies unsere{' '}
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan, #00c8ff)', textDecoration: 'underline' }}>
            Datenschutzerklärung
          </a>
          {' '}und die{' '}
          <a href="/nutzungsbedingungen" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan, #00c8ff)', textDecoration: 'underline' }}>
            Nutzungsbedingungen
          </a>
          {' '}und bestätige dein Einverständnis, um das Tool weiter nutzen zu können.
        </p>
        <button
          onClick={handleAccept}
          disabled={loading}
          style={{ width: '100%', padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(0,200,255,0.2), rgba(0,100,220,0.25))', border: '1px solid rgba(0,255,255,0.3)', color: 'var(--cyan, #00c8ff)', fontFamily: 'Outfit', fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Wird gespeichert...' : 'Ich habe beide Dokumente gelesen und akzeptiere sie'}
        </button>
      </div>
    </div>
  );
}

function AuthProvider({ children }) {
  const [user,  setUser]  = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('ct_token'));
  const [ready, setReady] = useState(false);
  const [deactivated, setDeactivated] = useState(false);
  const [dsgvoPending, setDsgvoPending] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('ct_user');
    if (token && saved) setUser(JSON.parse(saved));
    setReady(true);
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem('ct_token');
    localStorage.removeItem('ct_user');
    setToken(null);
    setUser(null);
    setDeactivated(false);
    setDsgvoPending(false);
  }, []);

  const forceLogoutDeactivated = useCallback(() => {
    localStorage.removeItem('ct_token');
    localStorage.removeItem('ct_user');
    setToken(null);
    setUser(null);
    setDeactivated(true);
  }, []);

  // Globale Referenzen setzen damit api() sie nutzen kann
  useEffect(() => {
    _globalLogout       = forceLogoutDeactivated;
    _globalLogoutNormal = logout;
  }, [forceLogoutDeactivated, logout]);

  // Beim App-Start aktiv prüfen ob Account noch aktiv ist + Tier aktualisieren
  useEffect(() => {
    if (!token) return;
    fetch('/api/auth/me', {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(async res => {
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        if (data.deactivated) forceLogoutDeactivated();
      } else if (res.status === 401) {
        sessionStorage.setItem('ct_session_msg', 'Sitzung abgelaufen. Bitte neu einloggen.');
        logout();
      } else if (res.ok) {
        const data = await res.json().catch(() => null);
        if (data) {
          const updated = { ...JSON.parse(localStorage.getItem('ct_user') || '{}'), ...data };
          localStorage.setItem('ct_user', JSON.stringify(updated));
          setUser(updated);
          if (!data.dsgvo_accepted_at) setDsgvoPending(true);
        }
      }
    }).catch(() => {});
  }, [token]);

  function login(token, user) {
    localStorage.setItem('ct_token', token);
    localStorage.setItem('ct_user', JSON.stringify(user));
    setToken(token);
    setUser(user);
    setDeactivated(false);
    if (!user.dsgvo_accepted_at) setDsgvoPending(true);
  }

  if (!ready) return null;

  // Deaktivierter Account — Sperrseite anzeigen
  if (deactivated) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050510' }}>
        <div style={{ maxWidth: 420, padding: 36, textAlign: 'center', background: 'rgba(255,40,40,0.06)', border: '1px solid rgba(255,40,40,0.2)', borderRadius: 16 }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>⊘</div>
          <h2 style={{ color: '#ff4444', fontFamily: 'Orbitron', fontSize: 18, marginBottom: 12 }}>Account deaktiviert</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            Dein Zugang wurde deaktiviert. Bitte melde dich bei look4more um den Zugang wiederherzustellen.
          </p>
          <a href="mailto:steffen.woik@look4more.de" style={{ display: 'inline-block', padding: '10px 24px', background: 'rgba(255,40,40,0.15)', border: '1px solid rgba(255,40,40,0.3)', borderRadius: 9, color: '#ff6666', textDecoration: 'none', fontSize: 13 }}>
            steffen.woik@look4more.de
          </a>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, isAuth: !!token, login, logout }}>
      {children}
      {dsgvoPending && token && <DsgvoModal onAccept={() => setDsgvoPending(false)} />}
    </AuthContext.Provider>
  );
}

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('ct_theme') || 'dark');
  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('ct_theme', next);
    document.documentElement.setAttribute('data-theme', next);
  }
  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); }, [theme]);
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// api() — fängt 401 (Token abgelaufen) und 403 (deactivated) ab
export async function api(path, options = {}, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res  = await fetch(`/api${path}`, { ...options, headers: { ...headers, ...options.headers } });
  const data = await res.json();
  if (res.status === 403 && data.deactivated) {
    if (_globalLogout) _globalLogout();
    throw new Error(data.error || 'Account deaktiviert');
  }
  if (res.status === 401 && token) {
    // Token abgelaufen → sichtbar ausloggen statt stille leere Seite
    sessionStorage.setItem('ct_session_msg', 'Sitzung abgelaufen. Bitte neu einloggen.');
    if (_globalLogoutNormal) _globalLogoutNormal();
    throw new Error(data.error || 'Sitzung abgelaufen');
  }
  if (!res.ok) throw new Error(data.error || 'Fehler');
  return data;
}

function Protected({ children }) {
  const { isAuth } = useAuth();
  return isAuth ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login"       element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/datenschutz"        element={<Datenschutz />} />
            <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
            <Route path="/admin"       element={<Admin />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index           element={<Dashboard />} />
              <Route path="generate" element={<Generate />} />
              <Route path="history"  element={<History />} />
              <Route path="video"    element={<VideoPost />} />
              <Route path="channels" element={<Channels />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
